import React from 'react';
import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CategoryType } from '@data/sdk/match';
import { Namespace } from '@data/utils/enums';
import {
  AntdCol,
  AntdDrawer,
  AntdForm,
  AntdInput,
  AntdInputNumber,
  AntdRow,
  AntdSelect,
  AntdSpace,
  AntdTypography,
  Button,
} from '@ui/components';
import { useCreateCategory } from './hooks';

const { Text } = AntdTypography;

type CreateCategoryProps = {
  close: () => void;
  open: boolean;
  questionId: string;
  type: CategoryType;
};

export const CreateCategory = ({ open, close, type, questionId }: CreateCategoryProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const {
    form,
    isLoading,
    isEditMode,
    categoriesOptions,
    categoriesLoading,
    initialValues,
    handleScroll,
    setIsEditMode,
    handleCreateCategory,
    handleSearchChange,
  } = useCreateCategory({
    close,
    questionId,
    type,
  });

  return (
    <AntdDrawer
      title={
        <Text className="font-medium text-xl">{t(`question_page.classification.add_${type}`)}</Text>
      }
      open={open}
      closeIcon={null}
      extra={
        isEditMode ? (
          <CloseOutlined onClick={() => setIsEditMode(false)} className="text-base-700" />
        ) : null
      }
      styles={{ header: { border: 'none' }, body: { paddingTop: 0 } }}
      width={600}
    >
      <AntdForm
        form={form}
        initialValues={initialValues}
        layout="vertical"
        autoComplete="off"
        requiredMark={false}
        className="w-full"
        onFinish={handleCreateCategory}
      >
        {isEditMode ? (
          <>
            <AntdRow gutter={12}>
              <AntdCol lg={18}>
                <AntdForm.Item
                  label={t('question_page.classification.form.name')}
                  name="name"
                  rules={[{ required: true, message: t('patient:validations.required') }]}
                >
                  <AntdInput />
                </AntdForm.Item>
              </AntdCol>
              <AntdCol lg={6}>
                <AntdForm.Item
                  label={t('question_page.classification.form.priority')}
                  rules={[{ required: true, message: t('patient:validations.required') }]}
                  name="priority"
                >
                  <AntdInputNumber className="w-full rounded-2xl" max={100} min={0} />
                </AntdForm.Item>
              </AntdCol>
            </AntdRow>

            <AntdRow gutter={12}>
              <AntdCol lg={24}>
                <AntdForm.Item
                  label={t('question_page.classification.form.short_name')}
                  name="shortName"
                >
                  <AntdInput />
                </AntdForm.Item>
              </AntdCol>
            </AntdRow>

            <AntdRow>
              <AntdCol lg={24}>
                <AntdForm.Item
                  label={t('question_page.classification.form.context')}
                  name="context"
                >
                  <AntdInput.TextArea
                    className="rounded-md"
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    maxLength={255}
                  />
                </AntdForm.Item>
              </AntdCol>
            </AntdRow>
          </>
        ) : (
          <AntdRow className="mt-[1px]">
            <AntdCol lg={24}>
              <AntdForm.Item name="category">
                <AntdSelect
                  allowClear
                  showSearch
                  options={categoriesOptions}
                  placeholder={t('patient:common.type_here')}
                  onSearch={handleSearchChange}
                  onPopupScroll={handleScroll}
                  loading={categoriesLoading}
                  className="w-full"
                  dropdownRender={(menu) => (
                    <>
                      <Button
                        type="text"
                        icon={<PlusOutlined />}
                        onClick={() => setIsEditMode(true)}
                        className="w-full justify-start text-primary-600"
                      >
                        {t(`question_page.classification.create_new_${type}`)}
                      </Button>
                      {menu}
                    </>
                  )}
                />
              </AntdForm.Item>
            </AntdCol>
          </AntdRow>
        )}

        <AntdSpace className="float-right">
          <Button type="primary" htmlType="submit">
            {t('question_page.classification.form.add')}
          </Button>
          <Button onClick={close} loading={isLoading}>
            {t('patient:common.cancel')}
          </Button>
        </AntdSpace>
      </AntdForm>
    </AntdDrawer>
  );
};
